/**
 * AskChefReviewPage — the HUMAN REVIEW screen for everything the chef serves
 * for free to everyone.
 *
 * The chef answers general cooking from AI knowledge and can be wrong, and the
 * shared recipe cache is served to every household at no cost. So every row
 * carries an honest state: Reviewed (a person signed it off), Awaiting review
 * (seeded, demo organizations may serve it) or Unreviewed (the model cached it
 * and nobody has looked — never served to anyone).
 *
 * The featured-suggestions table lives on the same screen, because until now
 * it was API-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    BadgeCheck, Clock, Eye, Loader2, Pencil, Plus, RefreshCw, Save, Sparkles, Trash2, X,
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const ORG = 'grocery-demo-org-0001';
const LANGS = ['en', 'fr', 'es'];
const STATES = {
    reviewed: { label: 'Reviewed', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200', Icon: BadgeCheck },
    awaiting_review: { label: 'Awaiting review', cls: 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100', Icon: Clock },
    unreviewed: { label: 'Unreviewed', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200', Icon: Eye },
};

const StatusPill = ({ status }) => {
    const s = STATES[status] || STATES.unreviewed;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${s.cls}`}
            data-testid={`recipe-status-${status}`}>
            <s.Icon className="h-3.5 w-3.5" />{s.label}
        </span>
    );
};

const RecipeRow = ({ row, onReview, onDelete, onRegenerate, onSave, busyKey }) => {
    const [edit, setEdit] = useState(false);
    const [draft, setDraft] = useState(row);
    const busy = busyKey === row.key;
    useEffect(() => { setDraft(row); }, [row]);

    return (
        <div className="space-y-2 rounded-2xl border border-slate-200 p-4 dark:border-slate-700"
            data-testid={`recipe-row-${row.key}`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold uppercase text-white dark:bg-white dark:text-slate-900">
                    {row.language}
                </span>
                <StatusPill status={row.review_status} />
                <span className="text-[11px] font-mono text-muted-foreground">{row.key}</span>
                {row.seeded ? (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">seeded</span>
                ) : (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                        {row.generated_by || 'model'}
                    </span>
                )}
                {row.reviewed_by ? (
                    <span className="text-[11px] text-muted-foreground">
                        by {row.reviewed_by} · {(row.reviewed_at || '').slice(0, 10)}
                    </span>
                ) : null}
            </div>

            {edit ? (
                <div className="space-y-2">
                    <Input value={draft.title || ''} data-testid={`recipe-title-input-${row.key}`}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        className="min-h-[44px] font-bold" />
                    <div className="flex gap-2">
                        <Input type="number" value={draft.servings || 4} className="min-h-[44px] max-w-[120px]"
                            data-testid={`recipe-servings-input-${row.key}`}
                            onChange={(e) => setDraft({ ...draft, servings: Number(e.target.value) })} />
                        <Input type="number" value={draft.time_minutes || 0} className="min-h-[44px] max-w-[140px]"
                            data-testid={`recipe-time-input-${row.key}`}
                            onChange={(e) => setDraft({ ...draft, time_minutes: Number(e.target.value) })} />
                    </div>
                    <Textarea rows={6} value={(draft.steps || []).join('\n')}
                        data-testid={`recipe-steps-input-${row.key}`}
                        onChange={(e) => setDraft({ ...draft, steps: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} />
                    <div className="flex gap-2">
                        <Button onClick={() => onSave(row.key, draft).then(() => setEdit(false))}
                            disabled={busy} data-testid={`recipe-save-${row.key}`}
                            className="min-h-[44px] gap-2 font-bold">
                            <Save className="h-4 w-4" />Save
                        </Button>
                        <Button variant="outline" onClick={() => { setDraft(row); setEdit(false); }}
                            data-testid={`recipe-cancel-${row.key}`} className="min-h-[44px] gap-2">
                            <X className="h-4 w-4" />Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-base font-bold text-foreground">{row.title}</p>
                    <p className="text-xs text-muted-foreground">
                        {row.servings} servings · {row.time_minutes || '?'} min · {(row.steps || []).length} steps
                        · {(row.ingredients || []).length} ingredients
                    </p>
                    <ol className="list-inside list-decimal space-y-0.5 text-xs text-muted-foreground">
                        {(row.steps || []).slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                    <div className="flex flex-wrap gap-2 pt-1">
                        {row.review_status === 'reviewed' ? (
                            <Button variant="outline" onClick={() => onReview(row.key, false)}
                                disabled={busy} data-testid={`recipe-unreview-${row.key}`}
                                className="min-h-[44px] gap-2 text-xs font-bold">
                                <X className="h-4 w-4" />Withdraw review
                            </Button>
                        ) : (
                            <Button onClick={() => onReview(row.key, true)} disabled={busy}
                                data-testid={`recipe-review-${row.key}`}
                                className="min-h-[44px] gap-2 bg-emerald-600 text-xs font-bold hover:bg-emerald-700">
                                <BadgeCheck className="h-4 w-4" />Mark reviewed
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setEdit(true)}
                            data-testid={`recipe-edit-${row.key}`}
                            className="min-h-[44px] gap-2 text-xs font-bold">
                            <Pencil className="h-4 w-4" />Edit
                        </Button>
                        <Button variant="outline" onClick={() => onRegenerate(row.key)} disabled={busy}
                            data-testid={`recipe-regenerate-${row.key}`}
                            className="min-h-[44px] gap-2 text-xs font-bold">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Regenerate
                        </Button>
                        <Button variant="outline" onClick={() => onDelete(row.key)} disabled={busy}
                            data-testid={`recipe-delete-${row.key}`}
                            className="min-h-[44px] gap-2 text-xs font-bold text-rose-600">
                            <Trash2 className="h-4 w-4" />Delete
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

const blankFeatured = () => ({
    trigger_keyword: '', suggest_catalog_id: '', brand_label: '', sponsored: false,
    active: true, text: { en: '', fr: '', es: '' },
});

const FeaturedEditor = ({ rows, onSave, onDelete, catalog, onSearch }) => {
    const [draft, setDraft] = useState(null);
    const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
    return (
        <section className="space-y-3" data-testid="featured-editor">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-foreground">Featured suggestions</h2>
                <Button onClick={() => setDraft(blankFeatured())} data-testid="featured-new-btn"
                    className="min-h-[44px] gap-2 font-bold">
                    <Plus className="h-4 w-4" />New row
                </Button>
            </div>
            <p className="text-sm text-muted-foreground">
                Cross-sell is DATA, never model output. A paid placement must carry Sponsored —
                that is what puts the visible label on screen, and it also stops the chef from
                naming that brand when someone asks for an opinion.
            </p>

            {draft ? (
                <div className="space-y-2 rounded-2xl border-2 border-indigo-300 p-4" data-testid="featured-form">
                    <Input placeholder="Trigger keyword (e.g. lasagna)" value={draft.trigger_keyword || ''}
                        data-testid="featured-keyword" className="min-h-[44px]"
                        onChange={(e) => set({ trigger_keyword: e.target.value })} />
                    <Input placeholder="Search the catalog…" data-testid="featured-catalog-search"
                        className="min-h-[44px]" onChange={(e) => onSearch(e.target.value)} />
                    <select value={draft.suggest_catalog_id || ''} data-testid="featured-catalog-select"
                        className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-background px-3 text-sm dark:border-slate-700"
                        onChange={(e) => set({ suggest_catalog_id: e.target.value })}>
                        <option value="">Choose the item to suggest…</option>
                        {catalog.map((c) => (
                            <option key={c.catalog_item_id} value={c.catalog_item_id}>
                                {c.name} ({c.catalog_item_id})
                            </option>
                        ))}
                    </select>
                    <Input placeholder="Brand label (optional)" value={draft.brand_label || ''}
                        data-testid="featured-brand" className="min-h-[44px]"
                        onChange={(e) => set({ brand_label: e.target.value })} />
                    {LANGS.map((l) => (
                        <Input key={l} placeholder={`Line shown to the customer · ${l.toUpperCase()}`}
                            value={draft.text?.[l] || ''} data-testid={`featured-text-${l}`}
                            className="min-h-[44px]"
                            onChange={(e) => set({ text: { ...draft.text, [l]: e.target.value } })} />
                    ))}
                    <label className="flex min-h-[44px] items-center gap-2 text-sm font-bold">
                        <input type="checkbox" checked={!!draft.sponsored} data-testid="featured-sponsored"
                            onChange={(e) => set({ sponsored: e.target.checked })} />
                        Sponsored (paid placement — shows the Sponsored label)
                    </label>
                    <label className="flex min-h-[44px] items-center gap-2 text-sm font-bold">
                        <input type="checkbox" checked={draft.active !== false} data-testid="featured-active"
                            onChange={(e) => set({ active: e.target.checked })} />
                        Active
                    </label>
                    <div className="flex gap-2">
                        <Button onClick={() => onSave(draft).then(() => setDraft(null))}
                            data-testid="featured-save" className="min-h-[44px] gap-2 font-bold">
                            <Save className="h-4 w-4" />Save row
                        </Button>
                        <Button variant="outline" onClick={() => setDraft(null)}
                            data-testid="featured-cancel" className="min-h-[44px] gap-2">
                            <X className="h-4 w-4" />Cancel
                        </Button>
                    </div>
                </div>
            ) : null}

            <div className="space-y-2">
                {rows.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 p-3 dark:border-slate-700"
                        data-testid={`featured-row-${r.id}`}>
                        <span className="text-sm font-bold text-foreground">
                            {r.trigger_keyword || r.trigger_catalog_id || 'any'} → {r.suggest_catalog_id}
                        </span>
                        {r.brand_label ? <span className="text-xs text-muted-foreground">{r.brand_label}</span> : null}
                        {r.sponsored ? (
                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-bold uppercase text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200">
                                sponsored
                            </span>
                        ) : null}
                        {r.active === false ? (
                            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-600">off</span>
                        ) : null}
                        <span className="w-full text-xs text-muted-foreground">{(r.text || {}).en}</span>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setDraft(r)}
                                data-testid={`featured-edit-${r.id}`} className="min-h-[44px] gap-2 text-xs font-bold">
                                <Pencil className="h-4 w-4" />Edit
                            </Button>
                            <Button variant="outline" onClick={() => onDelete(r.id)}
                                data-testid={`featured-delete-${r.id}`}
                                className="min-h-[44px] gap-2 text-xs font-bold text-rose-600">
                                <Trash2 className="h-4 w-4" />Delete
                            </Button>
                        </div>
                    </div>
                ))}
                {!rows.length ? (
                    <p className="text-sm text-muted-foreground" data-testid="featured-empty">
                        No featured rows yet for this organization.
                    </p>
                ) : null}
            </div>
        </section>
    );
};

export default function AskChefReviewPage() {
    const [recipes, setRecipes] = useState([]);
    const [counts, setCounts] = useState({});
    const [featured, setFeatured] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [lang, setLang] = useState('');
    const [status, setStatus] = useState('');
    const [busyKey, setBusyKey] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const params = {};
        if (lang) params.lang = lang;
        if (status) params.status = status;
        const [rec, feat] = await Promise.all([
            api.get('/ask-chef/admin/shared-recipes', { params }),
            api.get('/ask-chef/admin/featured', { params: { organization_id: ORG } }),
        ]);
        setRecipes(rec.data.recipes || []);
        setCounts(rec.data.counts || {});
        setFeatured(feat.data.featured || []);
        setLoading(false);
    }, [lang, status]);

    useEffect(() => { load().catch(() => { setLoading(false); toast.error('Super admin only'); }); }, [load]);

    const search = async (q) => {
        const r = await api.get('/ask-chef/admin/catalog-search', { params: { q } });
        setCatalog(r.data.results || []);
    };

    const review = async (key, reviewed) => {
        setBusyKey(key);
        try {
            await api.post('/ask-chef/admin/shared-recipes/review', { reviewed }, { params: { key } });
            toast.success(reviewed ? 'Marked reviewed' : 'Review withdrawn');
            await load();
        } catch (_e) { toast.error('Could not update'); } finally { setBusyKey(null); }
    };
    const remove = async (key) => {
        setBusyKey(key);
        try {
            await api.delete('/ask-chef/admin/shared-recipes', { params: { key } });
            toast.success('Deleted');
            await load();
        } catch (_e) { toast.error('Could not delete'); } finally { setBusyKey(null); }
    };
    const regenerate = async (key) => {
        setBusyKey(key);
        try {
            await api.post('/ask-chef/admin/shared-recipes/regenerate', { organization_id: ORG }, { params: { key } });
            toast.success('Regenerated — now unreviewed');
            await load();
        } catch (_e) { toast.error('Could not regenerate'); } finally { setBusyKey(null); }
    };
    const save = async (key, draft) => {
        setBusyKey(key);
        try {
            await api.put('/ask-chef/admin/shared-recipes', {
                title: draft.title, servings: draft.servings,
                time_minutes: draft.time_minutes, steps: draft.steps,
            }, { params: { key } });
            toast.success('Saved — still needs a review');
            await load();
        } catch (_e) { toast.error('Could not save'); } finally { setBusyKey(null); }
    };
    const saveFeatured = async (row) => {
        try {
            await api.post('/ask-chef/admin/featured', row, { params: { organization_id: ORG } });
            toast.success('Featured row saved');
            await load();
        } catch (_e) { toast.error('Could not save the row'); }
    };
    const deleteFeatured = async (id) => {
        try {
            await api.delete(`/ask-chef/admin/featured/${id}`);
            toast.success('Row deleted');
            await load();
        } catch (_e) { toast.error('Could not delete'); }
    };

    const total = useMemo(
        () => (counts.reviewed || 0) + (counts.awaiting_review || 0) + (counts.unreviewed || 0),
        [counts]);

    return (
        <div className="mx-auto max-w-5xl space-y-6 px-4 py-6" data-testid="ask-chef-review-page">
            <header className="space-y-1">
                <h1 className="flex items-center gap-2 text-2xl font-black text-foreground">
                    <Sparkles className="h-6 w-6 text-indigo-500" />
                    Ask your chef — recipe review
                </h1>
                <p className="text-sm text-muted-foreground">
                    The chef answers general cooking from AI knowledge and can be wrong, and the
                    shared cache is served free to every household. Nothing unreviewed is ever
                    served. Demo organizations may serve the seeded rows that are still awaiting
                    review; a real organization only ever serves a reviewed recipe.
                </p>
            </header>

            <div className="flex flex-wrap items-center gap-2" data-testid="review-counts">
                <StatusPill status="reviewed" /><span className="text-sm font-bold">{counts.reviewed || 0}</span>
                <StatusPill status="awaiting_review" /><span className="text-sm font-bold">{counts.awaiting_review || 0}</span>
                <StatusPill status="unreviewed" /><span className="text-sm font-bold">{counts.unreviewed || 0}</span>
                <span className="text-sm text-muted-foreground">of {total} rows</span>
            </div>

            <div className="flex flex-wrap gap-2">
                {['', ...LANGS].map((l) => (
                    <Button key={l || 'all'} variant={lang === l ? 'default' : 'outline'}
                        onClick={() => setLang(l)} data-testid={`review-lang-${l || 'all'}`}
                        className="min-h-[44px] text-xs font-bold uppercase">
                        {l || 'all languages'}
                    </Button>
                ))}
                {['', 'reviewed', 'awaiting_review', 'unreviewed'].map((s) => (
                    <Button key={s || 'any'} variant={status === s ? 'default' : 'outline'}
                        onClick={() => setStatus(s)} data-testid={`review-status-filter-${s || 'any'}`}
                        className="min-h-[44px] text-xs font-bold">
                        {s ? (STATES[s].label) : 'any status'}
                    </Button>
                ))}
            </div>

            {loading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid="review-loading" />
            ) : (
                <section className="space-y-3" data-testid="review-recipes">
                    <h2 className="text-lg font-bold text-foreground">
                        Shared recipes ({recipes.length})
                    </h2>
                    {recipes.map((r) => (
                        <RecipeRow key={r.key} row={r} onReview={review} onDelete={remove}
                            onRegenerate={regenerate} onSave={save} busyKey={busyKey} />
                    ))}
                    {!recipes.length ? (
                        <p className="text-sm text-muted-foreground" data-testid="review-empty">
                            Nothing matches that filter.
                        </p>
                    ) : null}
                </section>
            )}

            <FeaturedEditor rows={featured} onSave={saveFeatured} onDelete={deleteFeatured}
                catalog={catalog} onSearch={search} />
        </div>
    );
}
